import { useState, useEffect, useCallback } from 'react';
import { BarChart3, TrendingUp, Terminal, MapPin, Users, Banknote, Shield, AlertTriangle, LogOut, ArrowLeft, ChevronRight, Wifi, WifiOff, CircleDot } from 'lucide-react';
import OutreachTab from './OutreachTab';
import CasesTab from './CasesTab';
import FollowupTab from './FollowupTab';
import SearchRankingWidget from './SearchRankingWidget';

const API = process.env.REACT_APP_BACKEND_URL;

function LoginScreen({ onLogin }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            if (!res.ok) throw new Error('Ongeldige inloggegevens');
            const data = await res.json();
            localStorage.setItem('dashboard_token', data.token);
            onLogin(data.token);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-500/10 rounded-2xl mb-4">
                        <Shield className="w-7 h-7 text-emerald-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white font-['Outfit']">Recrasolutions</h1>
                    <p className="text-gray-500 text-sm mt-1">Investeerders Dashboard</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="text"
                        placeholder="Gebruikersnaam"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50"
                        data-testid="dashboard-login-username"
                    />
                    <input
                        type="password"
                        placeholder="Wachtwoord"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/50"
                        data-testid="dashboard-login-password"
                    />
                    {error && <p className="text-red-400 text-sm">{error}</p>}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                        data-testid="dashboard-login-btn"
                    >
                        {loading ? 'Bezig...' : 'Inloggen'}
                    </button>
                </form>
            </div>
        </div>
    );
}

function MetricCard({ label, value, icon: Icon, subtext, variant = 'default' }) {
    const colors = {
        default: 'border-gray-800 bg-gray-900',
        green: 'border-emerald-500/20 bg-emerald-500/5',
        amber: 'border-amber-500/20 bg-amber-500/5',
        blue: 'border-blue-500/20 bg-blue-500/5'
    };
    const iconColors = {
        default: 'text-gray-500',
        green: 'text-emerald-400',
        amber: 'text-amber-400',
        blue: 'text-blue-400'
    };
    return (
        <div className={`rounded-xl border p-5 ${colors[variant]}`}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className={`w-4 h-4 ${iconColors[variant]}`} />
                <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{label}</span>
            </div>
            <div className="text-2xl font-bold text-white font-['Outfit'] tabular-nums">{value}</div>
            {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
        </div>
    );
}

function MiniBar({ data, maxVal }) {
    const max = maxVal || Math.max(...data.map(d => d.transactions), 1);
    return (
        <div className="flex items-end gap-0.5 h-8">
            {data.map((d, i) => (
                <div
                    key={i}
                    className={`w-full rounded-t ${d.transactions > 0 ? 'bg-emerald-500/60' : 'bg-gray-800'}`}
                    style={{ height: `${Math.max(2, (d.transactions / max) * 100)}%` }}
                    title={`${d.month}: ${d.transactions}`}
                />
            ))}
        </div>
    );
}

function LocationRow({ loc, onClick }) {
    const statusColors = {
        active: 'bg-emerald-500/10 text-emerald-400',
        onboarding: 'bg-amber-500/10 text-amber-400',
        pending: 'bg-gray-800 text-gray-400'
    };
    const statusLabels = { active: 'Actief', onboarding: 'Onboarding', pending: 'In afwachting' };

    return (
        <div
            onClick={() => onClick(loc)}
            className="grid grid-cols-12 gap-3 items-center p-4 border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer transition-colors group"
            data-testid={`location-row-${loc.id}`}
        >
            <div className="col-span-3">
                <div className="font-medium text-white text-sm">{loc.naam}</div>
                <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{loc.plaats}</div>
            </div>
            <div className="col-span-1 text-center">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[loc.status]}`}>
                    {statusLabels[loc.status]}
                </span>
            </div>
            <div className="col-span-1 text-center text-sm text-gray-300 tabular-nums">{loc.terminals}</div>
            <div className="col-span-1 text-center text-sm text-gray-300 tabular-nums">{loc.avg_transactions_month}</div>
            <div className="col-span-2">
                <MiniBar data={loc.monthly_data} />
            </div>
            <div className="col-span-1 text-center">
                {loc.mrg_active && <AlertTriangle className="w-4 h-4 text-amber-400 mx-auto" />}
            </div>
            <div className="col-span-2 text-right text-sm font-bold text-emerald-400 tabular-nums">
                {cur(loc.total_monthly_revenue)}
            </div>
            <div className="col-span-1 text-right">
                <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
            </div>
        </div>
    );
}

function LocationDetail({ loc, onBack }) {
    return (
        <div className="space-y-6">
            <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm" data-testid="location-detail-back">
                <ArrowLeft className="w-4 h-4" /> Terug naar overzicht
            </button>
            <div className="flex items-center gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-white font-['Outfit']">{loc.naam}</h2>
                    <p className="text-gray-500 flex items-center gap-1"><MapPin className="w-4 h-4" />{loc.plaats}</p>
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <MetricCard label="Terminals" value={loc.terminals} icon={Terminal} />
                <MetricCard label="Gem. Transacties/mnd" value={loc.avg_transactions_month} icon={BarChart3} variant="blue" />
                <MetricCard label="Per Terminal/mnd" value={loc.transactions_per_terminal} icon={CircleDot} />
                <MetricCard label="Actieve Maanden" value={loc.active_months} icon={Users} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Recrasolutions Marge</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between"><span className="text-gray-500">Lease-inkomen</span><span className="text-white font-medium">{cur(loc.lease_income)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Investeerderskosten</span><span className="text-red-400 font-medium">-{cur(loc.investor_cost)}</span></div>
                        <div className="border-t border-gray-800 pt-3 flex justify-between"><span className="text-gray-400 font-semibold">Lease Marge</span><span className="text-emerald-400 font-bold">{cur(loc.lease_margin)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Transactie-inkomen</span><span className="text-emerald-400 font-medium">+{cur(loc.transaction_income)}</span></div>
                        {loc.mrg_active && <div className="flex justify-between"><span className="text-amber-400">MRG Inkomen</span><span className="text-amber-400 font-medium">+{cur(loc.mrg_income)}</span></div>}
                        <div className="border-t border-gray-800 pt-3 flex justify-between"><span className="text-white font-bold">Totaal Maandelijks</span><span className="text-emerald-400 font-bold text-lg">{cur(loc.total_monthly_revenue)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Totaal Jaarlijks</span><span className="text-emerald-300 font-bold">{cur(loc.total_annual_revenue)}</span></div>
                    </div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Transacties per Maand</h3>
                    <div className="flex items-end gap-1 h-40">
                        {loc.monthly_data.map((d, i) => {
                            const max = Math.max(...loc.monthly_data.map(m => m.transactions), 1);
                            return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                    <span className="text-xs text-gray-500 tabular-nums">{d.transactions > 0 ? d.transactions : ''}</span>
                                    <div
                                        className={`w-full rounded-t ${d.transactions > 0 ? 'bg-emerald-500/70' : 'bg-gray-800'}`}
                                        style={{ height: `${Math.max(4, (d.transactions / max) * 120)}px` }}
                                    />
                                    <span className="text-xs text-gray-600">{d.month}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {loc.mrg_active && (
                <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        <span className="font-semibold text-amber-400">MRG Actief</span>
                    </div>
                    <p className="text-sm text-amber-300/70">
                        Deze locatie zit onder het minimum van 500 transacties/maand. 
                        Huidige gemiddelde: {loc.avg_transactions_month}. Tekort: {Math.max(0, 500 - loc.avg_transactions_month)} transacties.
                        MRG inkomen: {cur(loc.mrg_income)}/maand.
                    </p>
                </div>
            )}
        </div>
    );
}

const cur = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v);

export default function Dashboard() {
    const [token, setToken] = useState(localStorage.getItem('dashboard_token'));
    const [data, setData] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [overviewRes, leadsRes] = await Promise.all([
                fetch(`${API}/api/dashboard/overview`, { headers }),
                fetch(`${API}/api/dashboard/leads`, { headers })
            ]);
            if (!overviewRes.ok) throw new Error('Unauthorized');
            setData(await overviewRes.json());
            if (leadsRes.ok) setLeads((await leadsRes.json()).leads || []);
        } catch {
            localStorage.removeItem('dashboard_token');
            setToken(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleLogout = () => {
        localStorage.removeItem('dashboard_token');
        setToken(null);
    };

    if (!token) return <LoginScreen onLogin={setToken} />;
    if (loading || !data) return (
        <div className="min-h-screen bg-gray-950 flex items-center justify-center">
            <div className="text-gray-500">Laden...</div>
        </div>
    );

    const { portfolio, financials, investment, locations, adyen_status } = data;

    return (
        <div className="min-h-screen bg-gray-950 text-white font-['Manrope']" data-testid="investor-dashboard">
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap');`}</style>
            
            {/* Header */}
            <header className="border-b border-gray-800 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                        <h1 className="font-bold text-white font-['Outfit']">Recrasolutions</h1>
                        <p className="text-xs text-gray-500">Investeerders Dashboard</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {/* Adyen status */}
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs ${adyen_status.connected ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-800 text-gray-500'}`}>
                        {adyen_status.connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                        {adyen_status.connected ? 'Adyen Live' : 'Demo Data'}
                    </div>
                    <div className="flex gap-1">
                        {[
                            ['overview', 'Portfolio'],
                            ['leads', 'Leads'],
                            ['outreach', 'Outreach'],
                            ['cases', 'Cases'],
                            ['followup', 'Follow-up'],
                        ].map(([tab, label]) => (
                            <button
                                key={tab}
                                onClick={() => { setActiveTab(tab); setSelectedLocation(null); }}
                                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${activeTab === tab ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'}`}
                                data-testid={`tab-${tab}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <button onClick={handleLogout} className="text-gray-500 hover:text-white transition-colors" data-testid="dashboard-logout">
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {activeTab === 'overview' && !selectedLocation && (
                    <div className="space-y-8">
                        {/* KPIs */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <MetricCard label="Live Teller" value={`${portfolio.active_terminals} geüpgraded`} icon={CircleDot} variant="green" subtext="Zichtbaar op landingspagina" />
                            <MetricCard label="Actieve Locaties" value={portfolio.active_locations} icon={MapPin} variant="green" subtext={`${portfolio.onboarding} onboarding, ${portfolio.pending} pending`} />
                            <MetricCard label="Actieve Terminals" value={portfolio.active_terminals} icon={Terminal} variant="blue" subtext={`${portfolio.total_terminals} totaal`} />
                            <MetricCard label="Maandelijkse Omzet" value={cur(financials.total_monthly_revenue)} icon={Banknote} variant="green" subtext={`${cur(financials.avg_revenue_per_terminal)} per terminal`} />
                            <MetricCard label="ROI (Jaarlijks)" value={`${investment.roi_annual_pct}%`} icon={TrendingUp} variant="green" subtext={`Terugverdientijd: ${investment.payback_months} mnd`} />
                        </div>

                        {/* Revenue Breakdown */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Lease Marge</h3>
                                <div className="text-2xl font-bold text-emerald-400 font-['Outfit']">{cur(financials.lease_margin_monthly)}</div>
                                <div className="text-xs text-gray-500 mt-1">{cur(LEASE_PRIJS - INVESTOR_COST)} x {portfolio.active_terminals} terminals</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Transactie-inkomen</h3>
                                <div className="text-2xl font-bold text-emerald-400 font-['Outfit']">{cur(financials.transaction_income_monthly)}</div>
                                <div className="text-xs text-gray-500 mt-1">{financials.avg_transactions_month} transacties x {cur(0.10)}</div>
                            </div>
                            <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">MRG Inkomen</h3>
                                <div className="text-2xl font-bold text-amber-400 font-['Outfit']">{cur(financials.mrg_income_monthly)}</div>
                                <div className="text-xs text-gray-500 mt-1">Van locaties onder minimum</div>
                            </div>
                        </div>

                        {/* Investment */}
                        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Investering & Rendement</h3>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                                <div><div className="text-xs text-gray-500 mb-1">Investering/Terminal</div><div className="text-white font-bold">{cur(investment.cost_per_terminal)}</div></div>
                                <div><div className="text-xs text-gray-500 mb-1">Totale Investering</div><div className="text-white font-bold">{cur(investment.total_investment)}</div></div>
                                <div><div className="text-xs text-gray-500 mb-1">Jaarlijks Rendement</div><div className="text-emerald-400 font-bold">{investment.roi_annual_pct}%</div></div>
                                <div><div className="text-xs text-gray-500 mb-1">Terugverdientijd</div><div className="text-white font-bold">{investment.payback_months} maanden</div></div>
                                <div><div className="text-xs text-gray-500 mb-1">Jaarlijkse Opbrengst</div><div className="text-emerald-400 font-bold">{cur(financials.total_annual_revenue)}</div></div>
                            </div>
                        </div>

                        {/* Locations Table */}
                        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-800">
                                <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Locaties ({locations.length})</h3>
                            </div>
                            <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs text-gray-600 uppercase tracking-wider border-b border-gray-800/50">
                                <div className="col-span-3">Locatie</div>
                                <div className="col-span-1 text-center">Status</div>
                                <div className="col-span-1 text-center">Term.</div>
                                <div className="col-span-1 text-center">Gem/mnd</div>
                                <div className="col-span-2 text-center">Trend</div>
                                <div className="col-span-1 text-center">MRG</div>
                                <div className="col-span-2 text-right">Omzet/mnd</div>
                                <div className="col-span-1"></div>
                            </div>
                            {locations.map(loc => (
                                <LocationRow key={loc.id} loc={loc} onClick={setSelectedLocation} />
                            ))}
                        </div>

                        {/* Adyen banner */}
                        {!adyen_status.connected && (
                            <div className="bg-gray-900 border border-dashed border-gray-700 rounded-xl p-6 text-center">
                                <WifiOff className="w-8 h-8 text-gray-600 mx-auto mb-3" />
                                <h3 className="text-gray-400 font-semibold mb-1">Adyen API niet verbonden</h3>
                                <p className="text-gray-600 text-sm">Verbind je Adyen account voor live transactiedata, onboarding status en real-time omzet per terminal.</p>
                                <p className="text-gray-700 text-xs mt-2">Huidige data is demo/simulatie</p>
                            </div>
                        )}

                        {/* Search Ranking */}
                        <SearchRankingWidget token={token} />
                    </div>
                )}

                {activeTab === 'overview' && selectedLocation && (
                    <LocationDetail loc={selectedLocation} onBack={() => setSelectedLocation(null)} />
                )}

                {activeTab === 'leads' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold font-['Outfit']">Leads ({leads.length})</h2>
                        {leads.length === 0 ? (
                            <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500">Geen leads gevonden</div>
                        ) : (
                            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="text-xs text-gray-600 uppercase border-b border-gray-800">
                                        <tr>
                                            <th className="px-4 py-3 text-left">Bedrijf</th>
                                            <th className="px-4 py-3 text-left">Email</th>
                                            <th className="px-4 py-3 text-left">Telefoon</th>
                                            <th className="px-4 py-3 text-center">Terminals</th>
                                            <th className="px-4 py-3 text-right">Netto Winst/mnd</th>
                                            <th className="px-4 py-3 text-left">Datum</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leads.map((lead, i) => (
                                            <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                                                <td className="px-4 py-3 text-white font-medium">{lead.bedrijfsnaam}</td>
                                                <td className="px-4 py-3 text-gray-400">{lead.email}</td>
                                                <td className="px-4 py-3 text-gray-400">{lead.telefoon}</td>
                                                <td className="px-4 py-3 text-center text-gray-300">{lead.aantal_douches || '-'}</td>
                                                <td className="px-4 py-3 text-right text-emerald-400 font-medium">{lead.netto_winst_maand ? cur(lead.netto_winst_maand) : '-'}</td>
                                                <td className="px-4 py-3 text-gray-500 text-xs">{lead.created_at ? new Date(lead.created_at).toLocaleDateString('nl-NL') : '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'outreach' && <OutreachTab token={token} />}
                {activeTab === 'cases' && <CasesTab token={token} />}
                {activeTab === 'followup' && <FollowupTab token={token} />}
            </main>
        </div>
    );
}

const LEASE_PRIJS = 39;
const INVESTOR_COST = 29;
